import { isApiError } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { useCallback } from "react";

/** API error codes with localized copy (backend `app/core/errors.py`). */
const CODE_KEYS: Readonly<Record<string, string>> = {
  rate_limited: "auth.errors.rateLimited",
  invalid_code: "auth.errors.invalidCode",
  code_expired: "auth.errors.codeExpired",
  too_many_attempts: "auth.errors.tooManyAttempts",
  weak_password: "auth.errors.weakPassword",
  breached_password: "auth.errors.breachedPassword",
  captcha_required: "auth.errors.captchaRequired",
  captcha_failed: "auth.errors.captchaFailed",
  grant_not_held: "roles.guardrails.grantNotHeld",
  self_role_edit: "roles.guardrails.selfRoleEdit",
  last_owner: "roles.guardrails.lastOwner",
  role_locked: "roles.guardrails.roleLocked",
  role_in_use: "roles.guardrails.roleInUse",
  impersonation_read_only: "roles.guardrails.readOnly",
  model_unavailable: "build.agentSettings.model.unavailableError",
  payment_method_disabled: "billing.methods.disabledError",
  billed_by_invoice: "billing.manual.gated",
};

/**
 * User-facing message for any thrown error: localized when we know the code, else the API's own
 * user-safe message (never a stack trace), else a generic line.
 */
export function useErrorMessage(): (error: unknown) => string {
  const { t } = useTranslation();
  return useCallback(
    (error: unknown) => {
      if (!isApiError(error)) return t("auth.errors.generic");
      if (error.kind === "network" || error.kind === "timeout") return t("auth.errors.network");
      if (error.code === "invalid_code") {
        const remaining = error.detailNumber("attempts_remaining");
        if (remaining !== undefined) return t("auth.errors.invalidCodeAttempts", { count: remaining });
      }
      const key = CODE_KEYS[error.code];
      if (key) return t(key);
      return error.message || t("auth.errors.generic");
    },
    [t],
  );
}
